import { faker } from '@faker-js/faker';
import set from 'lodash/set';

/**
 * Mock data generator
 * @param {Object} fields
 * @param {Number} limit
 * @returns {Promise<Array>}
 * @example
 * const data = await mock({
 *     id: 'investors.index:id'
 *     name: faker => faker.name.firstName(),
 *     slug: (faker, item) => item.name.replaceAll(' ', '-').toLowerCase()
 *     intro: 'Hello {{ person.prefix }} {{ person.lastName }}',
 *     status: faker => faker.helpers.arrayElement(['DRAFT', 'PUBLISHED', 'ARCHIVED']),
 * }, 10);
 */
export async function mock(fields, limit = 1) {
    const data = [];

    for (let i = 0; i < limit; i++) {
        const item = {};

        for (const field in fields) {
            try {
                item[`${field}`] = await getFieldValue(fields[`${field}`], item, mock);
            } catch (error) {
                console.error(error);
            }
        }

        data.push(item);
    }

    return data;
}

/**
 * Returns the value of a field, either by calling a function, or returning a string value.
 * @param {Function|String} fieldValue
 * @param {Object} item
 * @param {Function} mock
 * @returns {Promise<*>}
 */
export async function getFieldValue(fieldValue, item, mock) {
    let value = null;

    if (fieldValue === null) {
        value = null;
    } else if (typeof fieldValue === 'string') {
        value = await getStringValue(fieldValue);
    } else if (typeof fieldValue === 'object') {
        value = {};
        for (const key in fieldValue) {
            value[`${key}`] = await getFieldValue(fieldValue[`${key}`], item, mock);
        }
    } else if (typeof fieldValue === 'function') {
        if (fieldValue.prototype && fieldValue.prototype.constructor) {
            value = await getApiValue(fieldValue);
        } else {
            value = await fieldValue(faker, item, mock);
            value = await getStringValue(value);
            value = await getApiValue(value);
        }
    } else {
        value = fieldValue;
    }

    return value;
}

/**
 * Returns the string value of a field, replacing any faker placeholders.
 * @param {String} value
 * @returns {Promise<String>}
 */
export async function getStringValue(value) {
    if (typeof value === 'string') {
        if (value.includes('{{')) {
            value = faker.helpers.fake(value);
        }
    }

    return value;
}

/**
 * The API cache stores the results of API calls to avoid making the same call multiple times.
 * @type {Object}
 */
const API_CACHE = {};

/**
 * Returns the value of a field, either by calling a function, or returning a string value.
 * @param {Function|String} fieldValue
 * @param {Object} item
 * @param {Function} mock
 * @returns {Promise<*>}
 */
export async function getApiValue(value) {
    if (typeof value === 'function' && value.prototype && value.prototype.constructor) {
        const name = value.prototype.constructor.name;

        let items = [];

        if (API_CACHE[`${name}`] && API_CACHE[`${name}`].length) {
            items = API_CACHE[`${name}`];
        } else {
            items = (await new value().index(0, 10)).data;
            API_CACHE[`${name}`] = items;
        }

        return faker.helpers.arrayElement(items);
    }

    return value;
}

/**
 * Generates mock data based on provided headers and an optional preset.
 * @param {Array} headers - An array of header objects, each defining a data field.
 * @param {Object} preset - An optional object to pre-set specific field values.
 * @returns {Object} - A mock data object.
 */
export const generateMockDataFromHeaders = async (headers, preset = {}) => {
    const mockData = {
        id: faker.string.uuid()
    };

    for (const header of headers) {
        const dataKey = header.path ? header.path : header.column;

        let dataValue = null;

        if (header.mock) {
            dataValue = await getFieldValue(header.mock, mockData);
        } else {
            switch (header.format) {
                case 'string':
                    if (header.value.includes('email')) {
                        dataValue = faker.internet.email();
                    } else {
                        dataValue = faker.lorem.word();
                    }
                    break;
                case 'number':
                    dataValue = faker.number.int({ min: 1, max: 1000 });
                    break;
                case 'money':
                    dataValue = {
                        amount: faker.number.float({ min: 1000, max: 1000000, fractionDigits: 2 }),
                        currency: faker.helpers.arrayElement(['GBP'])
                    };
                    break;
                case 'date':
                    dataValue = faker.date.past();
                    break;
                case 'datetime':
                    dataValue = faker.date.recent();
                    break;
                case 'percentage':
                    dataValue = faker.datatype.number({ min: 0, max: 100 });
                    break;
                case 'boolean':
                    dataValue = faker.datatype.boolean();
                    break;
                case 'wysiwyg':
                    dataValue = faker.lorem.paragraph();
                    break;
                case 'enum':
                    dataValue = faker.helpers.arrayElement(Object.keys(header.enum));
                    break;
                default:
                    dataValue = null;
                    break;
            }
        }

        set(mockData, dataKey, dataValue);
    }

    for (const key in preset) {
        set(mockData, key, preset[`${key}`]);
    }

    return mockData;
};
